import type { NodeSn } from '../../../nodes';
import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';

export type BgpRoutingColumnValue = {
  name: string;
  type: string;
};

export type TableColumnFormatBgpRouting<TRow> = {
  type: 'bgpRouting';
  field: KeyOfType<TRow, BgpRoutingColumnValue | null>;
  toWebString: (value: BgpRoutingColumnValue) => string;
  snField: KeyOfType<TRow, NodeSn>;
};

export const createBgpRoutingColumn: {
  <TRow>(
    config: TableDataColumnConfig<TRow, BgpRoutingColumnValue | null> & {
      array?: false;
      toWebString: (value: BgpRoutingColumnValue) => string;
    },
  ): TableColumnBase<TRow, TableColumnFormatBgpRouting<TRow>>;
  <TRow>(
    config: TableDataColumnConfig<TRow, BgpRoutingColumnValue[] | null> & {
      array: true;
      toWebString: (value: BgpRoutingColumnValue) => string;
    },
  ): TableColumnBase<TRow, TableColumnFormatBgpRouting<TRow>>;
} = <TRow>(
  config: TableDataColumnConfig<TRow, BgpRoutingColumnValue | null> & {
    toWebString: (value: BgpRoutingColumnValue) => string;
  },
): TableColumnBase<TRow, TableColumnFormatBgpRouting<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'bgpRouting',
    field: config.key,
    toWebString: config.toWebString,
    snField: 'sn' as KeyOfType<TRow, NodeSn>,
  },
  csvStringDependencies: [config.key],
  webDependencies: [config.key, 'sn' as KeyOfType<TRow, NodeSn>],
  toCsvString: (row) => {
    const val = getValueFromKeyOfType(row, config.key);

    return val
      ? formatValueToCsvString(
          val as BgpRoutingColumnValue | BgpRoutingColumnValue[],
          config.toWebString,
        )
      : '';
  },
  filter: createDefaultFilter(config.filter, undefined),
});
